import {
  createAsyncThunk,
  createSlice,
  createSelector,
  PayloadAction,
} from '@reduxjs/toolkit';
import { merge, isEqual } from 'lodash';
import {
  ChatMessage,
  PerUserConversationStats,
  SubscribeUnsubscribeData,
} from 'sb-lib/types';
import {
  closeConversation,
  getConversation,
  getConversations,
  getConversationStats,
  getVisitorConversation,
  openConversation,
  postConversationAssigneeId,
  postConversationGroupId,
  putConversationRead,
} from 'shared/api';
import {
  Conversation,
  ConversationFilter,
  ConversationSort,
  ConversationStats,
  ConversationStatus,
  ConversationsUpdate,
  Page,
} from 'types';
import { AppState } from './reducers';
import { selectActiveUser } from './shared-selectors';

// STATE
export const initialState = {
  unreadInboxMessageCount: null as number | null,
  // Conversation
  isFetchingConversation: false,
  conversation: null as Conversation | null,
  // Conversations
  isFetchingConversations: false,
  conversations: [] as Conversation[],
  hasMoreConversations: false,
  isFetchingMoreConversations: false,
  conversationsFilter: null as ConversationFilter | null,
  conversationsStatus: null as ConversationStatus | null,
  conversationsSort: null as ConversationSort | null,
  conversationsGroupId: null as number | null,
  stats: null as ConversationStats | null,
  statsPerUser: null as PerUserConversationStats | null,
  // Visitor Conversations
  isFetchingVisitorConversations: false,
  visitorConversations: [] as Conversation[],
  visitorConversationsTotal: null as number | null,
  visitorConversationsError: null as string | null,
  hasMoreVisitorConversations: false,
  isFetchingMoreVisitorConversations: false,
  // Unread Conversations (assigned to agent, have unread visitor messages)
  isFetchingUnreadConversations: false,
  unreadConversations: {} as Record<Conversation['id'], Conversation>,
  unreadConversationsError: null as string | null,
  hasRecentUnseenUnassignedConversations: false,
  lastFetchConversationsArgs: null as FetchConversationsArgs | null,
};

export type ConversationState = typeof initialState;

// THUNKS
export const fetchConversationStats = createAsyncThunk<
  ConversationStats,
  { groupId: number | null },
  { state: AppState }
>('conversation/fetchConversationStats', async ({ groupId }) => {
  const res = await getConversationStats(groupId);
  return res.data;
});

export const fetchConversation = createAsyncThunk<
  Conversation,
  { visitorId: string; conversationId: number },
  { state: AppState }
>('conversation/fetchConversation', async ({ visitorId, conversationId }) => {
  const results = conversationId
    ? await getConversation(conversationId)
    : await getVisitorConversation(visitorId);
  return results.data;
});

export const replaceConversation = createAsyncThunk<
  Conversation,
  { conversationId: number },
  { state: AppState }
>('conversation/replaceConversation', async ({ conversationId }) => {
  const results = await getConversation(conversationId);
  return results.data;
});

export const setConversationRead = createAsyncThunk<
  void,
  { conversationId: number },
  { state: AppState }
>('conversation/setConversationRead', async ({ conversationId }) => {
  void putConversationRead(conversationId);
});

export const toggleConversation = createAsyncThunk<
  Conversation,
  { isOpen: boolean; conversationId: number }
>('conversation/toggleConversation', async ({ isOpen, conversationId }) => {
  const res = isOpen
    ? await closeConversation(conversationId)
    : await openConversation(conversationId);
  return res.data;
});

export const assignConversation = createAsyncThunk<
  Conversation,
  { conversationId: number; assigneeId: number | null }
>('conversation/assignConversation', async ({ conversationId, assigneeId }) => {
  const res = await postConversationAssigneeId({ conversationId, assigneeId });
  return res.data;
});

export const assignGroupConversation = createAsyncThunk<
  Conversation,
  { conversationId: number; groupId: number | null }
>('conversation/assignGroupConversation', async ({ conversationId, groupId }) => {
  const res = await postConversationGroupId({ conversationId, groupId });
  return res.data;
});

type FetchConversationsArgs = {
  page: number;
  filter: ConversationFilter;
  status: ConversationStatus;
  sort: ConversationSort;
  dateLastMessage?: number;
  userGroupId?: number;
};

export const fetchConversations = createAsyncThunk<
  Page<Conversation>,
  FetchConversationsArgs,
  { state: AppState }
>(
  'conversation/fetchConversations',
  async ({ page, filter, status, sort, dateLastMessage, userGroupId }, { getState }) => {
    const state = getState();
    const user = selectActiveUser(state);

    const res = await getConversations({
      page,
      userId: user?.id,
      filter,
      status,
      sort,
      dateLastMessage,
      userGroupId,
    });
    return res.data;
  },
);

export const fetchUnreadConversations = createAsyncThunk<
  Array<Conversation>,
  void,
  { state: AppState }
>('conversation/fetchUnreadConversations', async (_, { getState }) => {
  const state = getState();
  const user = selectActiveUser(state);
  const res = await getConversations({
    page: 0,
    userId: user?.id,
    filter: ConversationFilter.ASSIGNED,
    status: ConversationStatus.OPEN,
    sort: ConversationSort.NEWEST,
    dateLastMessage: 0,
    hasUnreads: true,
  });
  // Not paginated, so its just an array of convesations instead of a Page
  return (res.data as any) as Array<Conversation>;
});

export const fetchVisitorConversations = createAsyncThunk<
  Page<Conversation>,
  { visitorId: string; status: ConversationStatus; dateLastMessage?: number },
  { state: AppState }
>(
  'conversation/fetchVisitorConversations',
  async ({ visitorId, status, dateLastMessage }) => {
    console.log('$$$$$$$$$$$$$$$$$$$$$$$$$$$$$$$$$$');
    console.log(
      JSON.stringify({
        page: 1,
        visitorId,
        sort: ConversationSort.NEWEST,
        pageSize: 4,
        status,
        dateLastMessage,
      }),
    );
    const res = await getConversations({
      page: 1,
      visitorId,
      sort: ConversationSort.NEWEST,
      pageSize: 4,
      status,
      dateLastMessage,
    });
    return res.data;
  },
);

const updateUnreads = (state: ConversationState, message: ConversationsUpdate) => {
  const { data: conversation, type } = message;
  const unreadConversation = state.unreadConversations[conversation.id];
  if (unreadConversation) {
    const wasRemoved = type === 'REMOVE';
    const wasRead = type === 'UPDATE' && conversation.unreadMessageCounts.visitor === 0;
    if (wasRemoved || wasRead) {
      // Remove if visitor messages read or convo removed
      delete state.unreadConversations[conversation.id];
    } else if (type === 'UPDATE') {
      // Update unread conversation item if it was just updated
      state.unreadConversations[conversation.id] = conversation;
    }
  } else if (type === 'UPDATE' && conversation.unreadMessageCounts.visitor > 0) {
    // Add unread conversation
    state.unreadConversations[conversation.id] = conversation;
  }
};

// SLICE
const conversationSlice = createSlice({
  name: 'conversation',
  initialState,
  reducers: {
    receivedUnreadInboxMessageCount: (state, action: PayloadAction<number>) => {
      state.unreadInboxMessageCount = action.payload;
    },
    receivedConversationStats: (state, action: PayloadAction<ConversationStats>) => {
      state.stats = state.stats ? merge(state.stats, action.payload) : action.payload;
    },
    receivedConversationGroupStats: (
      state,
      action: PayloadAction<PerUserConversationStats>,
    ) => {
      if (!state.stats) {
        return;
      }

      if (!state.stats.extraUsers) {
        state.stats.extraUsers = [];
        return;
      }

      state.stats.extraUsers = state.stats.extraUsers.filter(
        u => u.id !== action.payload.id,
      );
      state.stats.extraUsers.push(action.payload);
    },
    receivedConversationsUpdate: (
      state,
      action: PayloadAction<SubscribeUnsubscribeData & { message: ConversationsUpdate }>,
    ) => {
      const { conversationsStatus, conversationsFilter } = state;
      const channelPart = `.${conversationsStatus}.${conversationsFilter}`;
      const { data: conversation, type } = action.payload.message;
      if (
        conversation.assigneeId === null &&
        conversation.unreadMessageCounts.visitor > 0 &&
        (conversation.lastMessage?.moderationStatus === 'approved' ||
          conversation.lastMessage?.moderationStatus === null)
      ) {
        state.hasRecentUnseenUnassignedConversations = true;
      }

      if (!action.payload.channelType.includes(channelPart)) {
        return;
      }
      const index = state.conversations?.findIndex(({ id }) => id === conversation.id);
      if (index > -1 && type === 'REMOVE') {
        state.conversations.splice(index, 1);
      } else if (index > -1 && type === 'UPDATE') {
        state.conversations.splice(index, 1, conversation);
      } else if (index === -1 && type === 'UPDATE') {
        state.conversations.unshift(conversation);
      }

      updateUnreads(state, action.payload.message);
    },
    receivedVisitorConversationsUpdate: (
      state,
      action: PayloadAction<SubscribeUnsubscribeData & { message: ConversationsUpdate }>,
    ) => {
      const { data: conversation, type } = action.payload.message;
      const index = state.visitorConversations?.findIndex(
        ({ id }) => id === conversation.id,
      );
      if (index > -1 && type === 'REMOVE') {
        state.visitorConversations.splice(index, 1);
        if (state.visitorConversationsTotal) {
          state.visitorConversationsTotal = state.visitorConversationsTotal - 1;
        }
      } else if (index > -1 && type === 'UPDATE') {
        state.visitorConversations.splice(index, 1, conversation);
      } else if (index === -1 && type === 'UPDATE') {
        state.visitorConversations.unshift(conversation);
        if (state.visitorConversationsTotal) {
          state.visitorConversationsTotal = state.visitorConversationsTotal + 1;
        }
      }
      if (
        state.conversation?.id === conversation.id &&
        state.conversation.assigneeId !== conversation.assigneeId &&
        type !== 'REMOVE'
      ) {
        state.conversation.assigneeId = conversation.assigneeId;
      }

      updateUnreads(state, action.payload.message);
    },
    setUnseenUnassignedConversations: (state, action: PayloadAction<boolean>) => {
      state.hasRecentUnseenUnassignedConversations = action.payload;
    },
    resetConversation: state => {
      state.conversation = null;
    },
    removeConversation: (state, action: PayloadAction<number>) => {
      state.conversations = state.conversations.filter(c => c.id !== action.payload);
    },
  },
  extraReducers: builder => {
    // fetchConversationStats
    builder.addCase(fetchConversationStats.fulfilled, (state, action) => {
      state.stats = action.payload;
    });
    // fetchConversation
    builder
      .addCase(fetchConversation.pending, state => {
        state.isFetchingConversation = true;
        state.conversation = null;
      })
      .addCase(fetchConversation.fulfilled, (state, action) => {
        state.isFetchingConversation = false;
        state.conversation = action.payload;
      })
      .addCase(fetchConversation.rejected, state => {
        state.isFetchingConversation = false;
      });
    // replaceConversation
    builder.addCase(replaceConversation.fulfilled, (state, action) => {
      state.conversation = action.payload;
    });
    // setConversationRead
    builder.addCase(setConversationRead.fulfilled, state => {
      if (state.conversation) {
        state.conversation = {
          ...state.conversation,
          unreadMessageCounts: { ...state.conversation?.unreadMessageCounts, visitor: 0 },
        };
      }
    });
    // toggleConversation
    builder.addCase(toggleConversation.fulfilled, (state, action) => {
      state.conversation = action.payload;
    });
    // assignConversation
    builder.addCase(assignConversation.fulfilled, (state, action) => {
      state.conversation = action.payload;
    });
    // assignGroupConversation
    builder.addCase(assignGroupConversation.fulfilled, (state, action) => {
      state.conversation = action.payload;
    });
    // fetchConversations
    builder
      .addCase(fetchConversations.pending, (state, action) => {
        state.isFetchingConversations = true;
        state.lastFetchConversationsArgs = action.meta.arg;

        if (!action.meta.arg.dateLastMessage) {
          state.conversations = [];
          state.hasMoreConversations = false;
          state.conversationsFilter = null;
          state.conversationsStatus = null;
          state.conversationsSort = null;
        } else {
          state.isFetchingMoreConversations = true;
        }
      })
      .addCase(fetchConversations.fulfilled, (state, action) => {
        const { items, page, pageSize, total } = action.payload;
        const { filter, status, sort, userGroupId } = action.meta.arg;
        if (!isEqual(action.meta.arg, state.lastFetchConversationsArgs)) {
          // Noop, let more recent thunk resolve
          return;
        }

        state.isFetchingConversations = false;
        state.isFetchingMoreConversations = false;

        if (action.meta.arg.dateLastMessage) {
          state.conversations = [...state.conversations, ...items];
        } else {
          state.conversations = items;
        }
        state.hasMoreConversations = (page - 1) * pageSize + items.length < total;
        state.conversationsFilter = filter;
        state.conversationsStatus = status;
        state.conversationsSort = sort;
        state.conversationsGroupId = userGroupId || null;
      })
      .addCase(fetchConversations.rejected, state => {
        state.isFetchingConversations = false;
        state.isFetchingMoreConversations = false;
        state.hasMoreConversations = false;
        state.conversationsFilter = null;
        state.conversationsStatus = null;
        state.conversationsSort = null;
      });
    // fetchVisitorConversations
    builder
      .addCase(fetchVisitorConversations.pending, (state, action) => {
        state.isFetchingVisitorConversations = true;
        state.visitorConversationsError = null;
        if (!action.meta.arg.dateLastMessage) {
          state.visitorConversations = [];
          state.hasMoreVisitorConversations = false;
          state.visitorConversationsTotal = null;
        } else {
          state.isFetchingMoreVisitorConversations = true;
        }
      })
      .addCase(fetchVisitorConversations.fulfilled, (state, action) => {
        const { items, page, pageSize, total } = action.payload;
        state.isFetchingVisitorConversations = false;
        state.isFetchingMoreVisitorConversations = false;
        if (action.meta.arg.dateLastMessage) {
          state.visitorConversations = [...state.visitorConversations, ...items];
        } else {
          state.visitorConversations = items;
        }
        state.hasMoreVisitorConversations = (page - 1) * pageSize + items.length < total;
        state.visitorConversationsError = null;
        state.visitorConversationsTotal = total;
      })
      .addCase(fetchVisitorConversations.rejected, (state, action) => {
        state.isFetchingVisitorConversations = false;
        state.isFetchingMoreVisitorConversations = false;
        state.hasMoreVisitorConversations = false;
        state.visitorConversationsError = action.error.message || null;
        state.visitorConversationsTotal = null;
      });
    // fetchUnreadConversations
    builder
      .addCase(fetchUnreadConversations.pending, (state, _) => {
        state.isFetchingUnreadConversations = true;
        state.unreadConversationsError = null;
      })
      .addCase(fetchUnreadConversations.fulfilled, (state, action) => {
        state.unreadConversations = action.payload.reduce(
          (acc, c) => ({ ...acc, [c.id]: c }),
          {},
        );
        state.isFetchingUnreadConversations = false;
      })
      .addCase(fetchUnreadConversations.rejected, (state, action) => {
        state.isFetchingUnreadConversations = false;
        state.unreadConversationsError =
          action.error.message || 'Unknown error fetchin unread conversations';
      });
  },
});

// SELECTORS
export const selectConversations = createSelector(
  (state: AppState) => state.conversation.conversations,
  (_: AppState, sort: ConversationSort) => sort,
  (conversations, sort) => {
    return [...conversations]
      .filter(({ lastMessage }) => !!lastMessage)
      .sort((a, b) => {
        const aDate = a.dateLastMessage || a.dateEnded || a.dateStarted;
        const bDate = b.dateLastMessage || b.dateEnded || b.dateStarted;
        return sort === ConversationSort.NEWEST ? bDate - aDate : aDate - bDate;
      });
  },
);

export const selectConversationGroupUsers = createSelector(
  (state: AppState) => state.conversation.conversationsGroupId,
  (state: AppState) => state.auth.userGroups,
  (conversationsGroupId, userGroups) => {
    return userGroups?.find(ug => ug.id === conversationsGroupId)?.users || null;
  },
);

export const selectVisitorConversations = createSelector(
  (state: AppState) => state.conversation.visitorConversations,
  conversations => {
    return [...conversations]
      .filter(({ lastMessage }) => !!lastMessage)
      .sort((a, b) => {
        const aDate = a.dateLastMessage || a.dateEnded || a.dateStarted;
        const bDate = b.dateLastMessage || b.dateEnded || b.dateStarted;
        return bDate - aDate;
      });
  },
);

export const selectVisitorConversation = createSelector(
  (state: AppState) => state.conversation.visitorConversations,
  (_: AppState, conversationId: number) => conversationId,
  (conversations, conversationId) => {
    return conversations.find(c => c.id === conversationId);
  },
);

export const selectMostRecentVisitorMessage = createSelector(
  (state: AppState) => state.conversation.visitorConversations,
  (_: AppState, sbid: string) => sbid,
  (conversations, sbid) => {
    let latestMessage: null | ChatMessage = null;
    conversations
      .filter(c => c.visitor.id == sbid)
      .forEach(c => {
        if (
          c.lastMessage?.sourceType === 'visitor' &&
          (!latestMessage || (c.lastMessage?.timestamp || 0) > latestMessage.timestamp)
        ) {
          latestMessage = c.lastMessage;
        }
      });
    return latestMessage ? (latestMessage as ChatMessage) : null;
  },
);

export const selectNextOpenConversation = createSelector(
  (state: AppState) => state.conversation.conversations,
  (_: AppState, currentConversationId: number) => currentConversationId,
  (conversations, currentConversationId) => {
    const currentConversationIndex = conversations.findIndex(
      c => c.id === currentConversationId,
    );
    if (conversations.length === 1 && currentConversationIndex === 0) {
      return null;
    }
    if (
      currentConversationIndex === -1 ||
      currentConversationIndex === conversations.length - 1
    ) {
      return conversations[0];
    }
    return conversations[currentConversationIndex + 1];
  },
);

// EXPORTS
export const {
  receivedUnreadInboxMessageCount,
  receivedConversationStats,
  receivedConversationGroupStats,
  receivedConversationsUpdate,
  receivedVisitorConversationsUpdate,
  resetConversation,
  setUnseenUnassignedConversations,
  removeConversation,
} = conversationSlice.actions;

export default conversationSlice.reducer;
